<template>
    <div class="container-fluid marketplace-scm">
        <div class="row">
            <div class="col-lg-12">
                <div class="card mb-3">
                    <div class="card-body">
                        <div class="d-flex flex-wrap align-items-center justify-content-between">
                            <div class="d-flex flex-wrap align-items-baseline mb-2">
                                <h4 class="mr-3 mb-0 text-nowrap">판매자배송 공급관리</h4>
                                <small class="text-muted">내가 적은 실제 재고로 남은 일수 · {{ vendorId || '판매자코드 없음' }}</small>
                            </div>
                            <div class="d-flex flex-wrap align-items-center mb-2">
                                <b-form-input v-model="keyword" size="sm" placeholder="상품명·옵션ID·바코드" class="mr-2 mb-1 marketplace-scm__search" />
                                <b-button variant="primary" size="sm" class="mb-1" :disabled="busy" @click="search">
                                    <i class="fas fa-search mr-1"></i> 조회
                                </b-button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="col-lg-12" v-if="busy">
                <div class="text-center text-muted py-3">상품·재고 조회 중...</div>
            </div>
            <div class="col-lg-12" v-if="errorText">
                <b-alert show variant="danger" dismissible @dismissed="errorText = ''">{{ errorText }}</b-alert>
            </div>

            <div class="col-lg-12" v-if="loaded">
                <div class="card">
                    <div class="card-body">
                        <div v-if="!rows.length" class="text-center py-5">
                            <h5 class="mb-2">아직 모은 판매자배송 상품이 없습니다</h5>
                            <p class="text-muted mb-0">판매자배송 › 상품관리에서 «상품 모으기» 를 먼저 눌러 주세요.</p>
                        </div>
                        <template v-else>
                            <div class="small text-muted mb-2">
                                <div>남은 일수 = 내가 적은 실제 재고 ÷ 최근 30일 하루 평균 판매</div>
                                옵션 {{ rows.length.toLocaleString() }}개 · 판매는 판매장부(Open API 매출) {{ from30 }} ~ 오늘 ·
                                쿠팡에 적힌 재고가 {{ fakeMin.toLocaleString() }}개 이상이면 실재고가 아닌 «쿠팡 표시 재고» 로 보고 계산에서 뺍니다
                            </div>
                            <b-table :items="shown" :fields="fields" small responsive stacked="md" striped hover>
                                <template #cell(name)="d">
                                    <div class="marketplace-scm__name">{{ d.item.productName }}</div>
                                    <div class="small text-muted">{{ d.item.itemName }} · {{ d.item.vendorItemId }}</div>
                                </template>
                                <!-- b-table 은 null 칸을 '' 로 넘긴다 — 빈 값 가름은 d.item 원값으로 -->
                                <template #cell(stockQuantity)="d">
                                    <span v-if="empty(d.item.stockQuantity)" class="text-muted">안 받음</span>
                                    <template v-else>
                                        <span>{{ count(d.item.stockQuantity) }}</span>
                                        <b-badge v-if="d.item.fakeStock" variant="warning" class="ml-1">쿠팡 표시 재고(실재고 아님)</b-badge>
                                    </template>
                                </template>
                                <template #cell(myStock)="d">
                                    <span v-if="empty(d.item.myStock)" class="text-muted">안 적음</span>
                                    <span v-else>{{ count(d.item.myStock) }}</span>
                                    <b-button v-if="d.item.canCopy" variant="outline-primary" size="sm" class="ml-2 py-0 text-nowrap" :disabled="saving === d.item.vendorItemId" @click="copyCoupangStock(d.item)">
                                        쿠팡 재고 그대로 쓰기
                                    </b-button>
                                </template>
                                <template #cell(units30)="d">{{ empty(d.item.units30) ? '안 모음' : count(d.item.units30) }}</template>
                                <template #cell(dailyAvg)="d">{{ empty(d.item.dailyAvg) ? '안 모음' : d.item.dailyAvg }}</template>
                                <template #cell(days)="d">
                                    <span v-if="empty(d.item.days)" class="text-muted">{{ d.item.why }}</span>
                                    <b-badge v-else :variant="d.item.days <= 7 ? 'danger' : d.item.days <= 30 ? 'warning' : 'success'">{{ d.item.days }}일</b-badge>
                                </template>
                            </b-table>
                        </template>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios'
import { buildScmRows, MARKETPLACE_FAKE_STOCK_MIN } from '@/utils/marketplaceScm'

function ymd(d) {
    const p = (n) => String(n).padStart(2, '0')
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

export default {
    name: 'MarketplaceScm',
    data() {
        return {
            vendorId: this.$store.state.user.suppliercode || '',
            keyword: '',
            products: [],
            from30: '',
            loaded: false,
            busy: false,
            saving: '',
            errorText: '',
            fakeMin: MARKETPLACE_FAKE_STOCK_MIN,
            fields: [
                { key: 'name', label: '상품명 / 옵션' },
                { key: 'stockQuantity', label: '쿠팡에 적힌 재고', class: 'text-right' },
                { key: 'myStock', label: '내가 적은 실제 재고' },
                { key: 'units30', label: '30일 판매량', class: 'text-right' },
                { key: 'dailyAvg', label: '하루 평균', class: 'text-right' },
                { key: 'days', label: '남은 일수' },
            ],
        }
    },
    computed: {
        rows() { return buildScmRows(this.products) },
        shown() {
            const k = this.keyword.trim()
            if (!k) return this.rows
            return this.rows.filter((p) => [p.productName, p.itemName, p.vendorItemId, p.barcode, p.sellerProductId].some((v) => String(v || '').includes(k)))
        },
    },
    mounted() {
        if (!this.vendorId) {
            this.errorText = '이 쿠플러스 계정에 판매자코드가 없습니다.'
            return
        }
        this.search()
    },
    methods: {
        empty(v) { return v === null || v === undefined || v === '' },
        count(v) { return Number(v).toLocaleString() },
        async search() {
            if (!this.vendorId) return
            this.busy = true
            this.errorText = ''
            try {
                const r = await axios.post('/api/marketplace/listProducts', { vendorId: this.vendorId, today: ymd(new Date()) })
                if (!r.data || !r.data.success) throw new Error((r.data && r.data.message) || '조회 실패')
                this.products = r.data.products
                this.from30 = r.data.from30
                this.loaded = true
            } catch (e) {
                console.error('[판매자배송 공급관리] 조회 실패:', e)
                this.errorText = '상품·재고를 불러오지 못했습니다: ' + ((e.response && e.response.data && e.response.data.message) || e.message)
            } finally {
                this.busy = false
            }
        },
        // 실제 재고로 쓰는 셀러 — 쿠팡에 적힌 재고를 «내가 적은 실제 재고» 로 한 번에 옮김(상품관리와 같은 저장 길)
        async copyCoupangStock(row) {
            this.saving = row.vendorItemId
            try {
                const r = await axios.post('/api/marketplace/saveStockMemo', { vendorId: this.vendorId, vendorItemId: row.vendorItemId, myStock: Number(row.stockQuantity) })
                if (!r.data || !r.data.success) throw new Error((r.data && r.data.message) || '저장 실패')
                const p = this.products.find((x) => String(x.vendorItemId) === String(row.vendorItemId))
                if (p) p.myStock = r.data.myStock
            } catch (e) {
                console.error('[판매자배송 공급관리] 재고 저장 실패:', e)
                this.errorText = '실제 재고를 저장하지 못했습니다: ' + ((e.response && e.response.data && e.response.data.message) || e.message)
            } finally {
                this.saving = ''
            }
        },
    },
}
</script>

<style lang="scss" scoped>
.marketplace-scm {
    &__search { width: 200px; }
    &__name { max-width: 320px; white-space: normal; }
    // 390 stacked 머리 칸(data-label) — 한글 낱말 가운데서 안 꺾이게
    ::v-deep th, ::v-deep td, ::v-deep td[data-label]::before { word-break: keep-all; }
    @media (max-width: 767.98px) { ::v-deep .b-table-stacked-md > tbody > tr + tr { border-top: 3px solid #dee2e6; } }
}
</style>
